/* eslint-disable @typescript-eslint/no-explicit-any */

import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { formatCamelCase, formatContractDate, formatTerm, withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
    resolveServiceStatusTag,
    serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";

export interface InternetConnection {
  id: number;
  connectionId: string;
  renewDateRaw: Date | null;
  mbpsNum: number;
  customer?: {
    name: string;
    id: number;
    chargebeeCustomerId: string
  };
  renewDate: string;
  terms: string;
  serviceType: string;
  name: string;
  tagsRich?: ResourceTagSummary[];
  currentStatus: string;
  internetCapacity: string;
  routing: string;
  vlan: string;
  parentConnection: string;
  parentConnectionId: string;
  parentConnectionType?: "PORT" | "LAG" | "FWaaS" | "SDWAN_HUB";
  parentConnectionSubid?: string;
  mbps: string;
  subscriptionId: string;
  status: string;
  statusMessage?: string | null;
  sdwanHub?: { id: number; name: string; subscriptionId: string | null } | null;
}

// Coercers
function toDateOrNull(s: unknown): Date | null {
  if (!s) return null;
  const d = new Date(String(s));
  return isNaN(d.getTime()) ? null : d;
}
function toNumberOrZero(x: unknown): number {
  if (typeof x === "number") return Number.isFinite(x) ? x : 0;
  const n = Number(String(x ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}


export const connectionColumns: GenericTableColumn<InternetConnection>[] = [
  {
    header: "Connection ID",
    accessor: "subscriptionId",
  },
  {
    header: "Parent Connection",
    accessor: "parentConnection",
  },
  {
    header: "Name",
    renderCell: (row) => {
      const nameValue = row.name;
      // If it's null, empty string, or an empty array → just dash
      if (
        nameValue == null ||
        (typeof nameValue === "string" && nameValue.trim() === "") ||
        (Array.isArray(nameValue) && nameValue.length === 0)
      ) {
        return <span className="text-semantic-text-secondary">-</span>;
      }
      // Otherwise render GenericTag
      const label = Array.isArray(nameValue) ? nameValue.join(" ") : nameValue;
      return (
        <div className="flex items-center">
          <GenericTag variant="neutral" label={label} size="small" />
        </div>
      );
    },
  },
  {
    header: "Mbps",
    accessor: "internetCapacity",
  },
  {
    header: "VLAN",
    accessor: "vlan",
  },
  {
    id: "connection_status",
    header: "Connection Status",
    renderCell: (row) => {
      const status = row.status;
      const { label, variant } = resolveServiceStatusTag(status);
      return <GenericTag variant={variant} label={label} size="small" />;
    },
    //Sort by a numeric rank (so "Active" > "Warning" > "Down" > "Unknown")
    sortAccessor: (row) => serviceStatusRank(row.status),
  },
  {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
  },
  {
    header: "Routing",
    accessor: "routing",
  },
  {
    header: "Term",
    accessor: "terms",
  },
  {
    header: "Renew Date",
    accessor: "renewDate",
  },
];


export const shapeInternetData = (connections: any[]): InternetConnection[] => {
  return connections.map((c) => {
    // Compute renewDate, parentConnection, tag, etc.
    let parentConnection = "";
    let parentConnectionId = "-";
    let parentConnectionType: "PORT" | "LAG" | "FWaaS" | "SDWAN_HUB" | undefined;
    let parentConnectionSubid: string | undefined;

    if (c.kontrolEdgeLag) {
      parentConnectionType = "LAG";
      parentConnectionSubid = String(c.kontrolEdgeLag.subid ?? "");
      parentConnection = `[LAG] ${parentConnectionSubid || "-"}`;
      parentConnectionId = String(c.kontrolEdgeLag.id ?? "-");
    } else if (c.kontrolEdgePort) {
      parentConnectionType = "PORT";
      parentConnectionSubid = String(c.kontrolEdgePort.subid ?? "");
      parentConnection = `[PORT] ${parentConnectionSubid || "-"}`;
      parentConnectionId = String(c.kontrolEdgePort.id ?? "-");
    } else if (c.fwaas) {
      parentConnectionType = "FWaaS";
      const serviceName = c.fwaas.name ?? String(c.fwaas.id ?? "-");
      parentConnection = `[FWaaS] ${serviceName}`;
      parentConnectionId = String(c.fwaas.id ?? "-");
      // no subid for FWaaS
    } else if (c.sdwanHub) {
      parentConnectionType = "SDWAN_HUB";
      const hubName = c.sdwanHub.name ?? String(c.sdwanHub.id ?? "-");
      parentConnection = `[SDWAN_HUB] ${hubName}`;
      parentConnectionId = String(c.sdwanHub.id ?? "-");
    }

    const customer = c.customer
    ? {
      name: c.customer.name ?? "-",
      id: c.customer.id ?? "-",
      chargebeeCustomerId: c.customer.chargebeeCustomerId ?? "-"
      }
    : undefined;

    const renewDateRaw = toDateOrNull(c.renewDate);
    const mbpsNum = toNumberOrZero(c.mbps);
    const tagsRich = mapKeListResourceTags(c);

    return {
      id: c.id ?? 0,
      connectionId: withFallback(c.connectionId),
      name: withFallback(c.name),
      customer,
      renewDateRaw,
      mbpsNum,
      renewDate: formatContractDate(c.renewDate),
      terms: formatTerm(c.term, c.term_unit),
      serviceType: withFallback(c.serviceType),
      currentStatus: withFallback(c.currentStatus),
      internetCapacity: withFallback(c.mbps),
      routing: c.routing ? formatCamelCase(c.routing) : "-",
      vlan: withFallback(c.vlan),
      mbps: withFallback(c.mbps),
      parentConnection,
      parentConnectionId,
      parentConnectionType,
      parentConnectionSubid,
      tagsRich,
      subscriptionId: withFallback(c.subscriptionId),
      status: withFallback(c.status),
      statusMessage: c.statusMessage ?? null,
      sdwanHub: c.sdwanHub ?? null,
    };
  });
};
